// tokens.js — iYuga Web design-tokens: the single source of truth.
// ISP-4018 / WL-001 (Fable Hail Mary Wave 1 foundation).
//
// WHY THIS FILE EXISTS
// The app already has an authoritative COLOR palette in `src/utils/theme.js`
// (`colors` + `Muitheme`), but had NO single-source-of-truth for spacing,
// radius, shadow, motion, typography scale, breakpoints or z-index — those
// values were re-derived as literals per component. This module centralises
// them (and re-exports `colors`) so every Wave-2 screen composes from ONE
// conformant token set instead of scattered magic numbers.
//
// ADDITIVE + IMPORT-SAFE: this introduces new tokens; it does not change any
// existing color value or MUI palette. Existing components keep working.
//
// Values are derived from the AS-IS design system documented in
// C:\iYuga\design-handoff\01-DESIGN-PRINCIPLES.md (verified against the live repo):
//   font  = 'DM Sans', sans-serif   · brand purple = #9B5AF8 (colors.PrimaryPurple)
//   radius = 4px controls / 8px cards+dialogs  · spacing = 4-based rhythm (16/24px)
//   motion = ~0.2s ease (Button fade)          · breakpoints from utils/device.js
//
// NOTE: this module is deliberately self-contained (it does NOT import `colors`
// from utils/theme.js) so that utils/theme.js can import IT without a circular
// dependency. `colors` and `tokens` are both re-exported from the `utils` barrel:
//   import { colors, tokens } from "../../utils";
// The Muitheme also carries them: theme.tokens (incl. theme.tokens.colors).

import { size as breakpointSizes } from "../utils/device";

// ── Typography ──────────────────────────────────────────────────────────────
// Family MUST match the app: 'DM Sans' (note the space) + sans-serif fallback.
export const fontFamily = "'DM Sans', sans-serif";

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

// Type scale mirrors the shared `TextView` variants (src/components/TextView),
// so tokenised text stays consistent with the app's existing copy component.
// size in px (base, pre-responsive) / weight.
export const typography = {
  largeTitle: { fontSize: 24, fontWeight: fontWeight.bold, lineHeight: 1.25 },
  title: { fontSize: 24, fontWeight: fontWeight.bold, lineHeight: 1.25 },
  header3: { fontSize: 20, fontWeight: fontWeight.bold, lineHeight: 1.3 },
  subtitle: { fontSize: 16, fontWeight: fontWeight.medium, lineHeight: 1.4 },
  largeText: { fontSize: 14, fontWeight: fontWeight.regular, lineHeight: 1.45 },
  text: { fontSize: 12, fontWeight: fontWeight.regular, lineHeight: 1.5 },
  button: { fontSize: 14, fontWeight: fontWeight.medium, lineHeight: 1 },
  tag: { fontSize: 10, fontWeight: fontWeight.bold, lineHeight: 1.4 },
};

// ── Spacing ─────────────────────────────────────────────────────────────────
// 4-based rhythm seen across the app (padding/margin/gap 4/8/12/16/24/32).
// Numeric px values; use `space(n)` for arbitrary multiples of the 4px base.
export const spacing = {
  none: 0,
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48,
};
export const spaceUnit = 4;
export const space = (n) => `${spaceUnit * n}px`;

// ── Horizontal gutter ───────────────────────────────────────────────────────
// A `vw`-only side gutter (`padding: 3vw`, `margin: 0 2vw`) scales linearly to
// nothing as the viewport narrows. MEASURED on web-stg 2026-07-24, Settings
// `CenterWrapper` (`padding-left: 3vw`): 43.2px @1440 -> 23.0px @768 ->
// 12.4px @414 -> 11.7px @390. That collapse IS the "left margin disappears on
// responsive views" defect: the gutter keeps shrinking while fixed-px content
// beside it does not, so the page reads as flush-left on small screens.
//
// `gutterX` keeps the fluid growth on wide screens but FLOORS it at a spacing
// token, so a narrow viewport can never lose its side margin. The default floor
// is `spacing.lg` (16px) — the value `Settings/NotificationPreferences` already
// hard-codes in its own `@media (max-width: 768px)` override, i.e. the app's
// existing, already-intended mobile gutter. Above the floor the value is
// byte-for-byte what it was before, so wide layouts do not move.
export const gutterX = (fluid = "3vw", min = spacing.lg) =>
  `max(${min}px, ${fluid})`;

// ── Radius ──────────────────────────────────────────────────────────────────
// 4px = inputs/buttons · 8px = cards/dialogs (matches live components).
export const radius = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  pill: 9999,
  round: "50%",
};

// ── Focus ring ────────────────────────────────────────────────────────────────
// THE colour of the app's keyboard focus indicator. ONE value, read by
// `brand.focusRing`, by `shadow.focus`, and by every call site that paints a
// ring. Declared above `shadow` only because `shadow.focus` derives from it.
//
// WHY #9B5AF8 AND NOT #CBB4FE (Gordon's decision, 2026-08-02).
// WCAG 2.2 SC 1.4.11 Non-text Contrast requires the indicator to clear 3:1
// against its ADJACENT colour(s). Measured (relative-luminance, probe validated
// against black/white=21:1 and #777/white=4.48:1 before any reading was trusted):
//
//     colour    vs #FFFFFF page   vs #8F38EF filled-button fill
//     #CBB4FE       1.83  FAIL            2.89  FAIL     <- the old value
//     #9B5AF8       4.03  PASS            1.31  FAIL
//
// #CBB4FE cleared NEITHER boundary anywhere in the app — it failed all 11
// measured focus call sites. #9B5AF8 clears 8 of those 11 outright, and on the
// other 3 it still improves from 0-of-2 boundaries to 1-of-2. It is also already
// the colour the app-wide primitive (`src/styles/focusRing.css`) ships live, so
// pointing the token here collapses the app to ONE ring colour instead of two.
//
// D8 IS NOW RESOLVED — GEOMETRY, NOT COLOUR (Gordon, 2026-08-02).
// A previous version of this comment said the remaining flush call sites were
// "UNSOLVABLE BY COLOUR". That half was right and is still true: clearing 3:1
// against BOTH the page and a #8F38EF fill needs relative luminance <= 0.016,
// i.e. a near-black ring. What it got wrong was implying the only geometric fix
// was `outline-offset`, whose gap is TRANSPARENT and therefore only works when
// the thing behind it happens to be light.
//
// Gordon chose the GitHub / Stripe / Apple treatment instead: a PAINTED white
// inner band, then the purple band — `0 0 0 2px #FFF, 0 0 0 4px #9B5AF8`. The
// purple's inner neighbour is then always white and its outer neighbour is
// always the page, whatever the control is filled with. That is why this works
// on ANY background and an offset gap does not.
//
// MEASURED on live staging bundle main.cfc59c6c.js, 2026-08-02, HEADED Chromium,
// REAL Tab presses (probe validated first: black/white = 21.00, #777/white =
// 4.48 — both correct, so a reading of "fails" is the target and not the tool):
//
//   boundary                                              ratio   verdict
//   #9B5AF8  vs the real page #F6F7F9                      3.76   PASS
//   #9B5AF8  vs the white inner band                       4.03   PASS
//   #FFFFFF  vs "Skip to main content" fill  #6C1FB8       8.31   PASS
//   #FFFFFF  vs "Create Wheel" fill          #8F38EF       5.28   PASS
//   #FFFFFF  vs help-FAB fill                #123051      13.40   PASS
//   -- what the OLD flush single ring did, same controls --
//   #9B5AF8  vs #6C1FB8  (skip link, measured live as broken)   2.06   FAIL
//   #9B5AF8  vs #8F38EF                                          1.31   FAIL
//
// NOTE the page is #F6F7F9, not #FFFFFF. Earlier passes quoted 4.03 against a
// white page; the real measured number is 3.76. It still clears 3:1 — but the
// number that was being quoted was against a background the app does not have.
//
// `colors.PrimaryPurple300` (#CBB4FE) is deliberately UNCHANGED: it has ~50
// decorative uses (borders, tints, icons, empty-state art) that are not focus
// rings and must not move.
export const focusRingColor = "#9B5AF8";

// The painted inner band. It is the app's page/surface colour rather than a new
// brand value: its ONLY job is to guarantee a known neighbour between the
// control's own fill and the purple band. Do not tint it — a tinted band stops
// being a reliable neighbour and the contrast table above stops holding.
export const focusRingInnerColor = "#FFFFFF";

/** px. Outer (purple) band. */
export const focusRingWidth = 2;
/** px. Inner (white) band, and therefore also the `outline-offset` the outline
 *  form must use so the two treatments describe the SAME geometry. */
export const focusRingInnerWidth = 2;

/**
 * px. ON-BRAND corner radius for the keyboard focus ring — the brand CONTROL
 * radius (`radius.sm` = 4px, the "4px controls" token), NOT a new value.
 *
 * WHY IT EXISTS (DL-096, Gordon 2026-08-02, judging the LIVE ring): the app-wide
 * ring is an `outline` + `box-shadow`, and BOTH follow the focused element's own
 * `border-radius`. For a text field the focusable node is the inner `<input>`,
 * which is measured SQUARE (`border-top-left-radius: 0px`) even though its
 * `.MuiOutlinedInput-root` container is rounded to 4px — so the ring paints a
 * SQUARE box hugging the text. Gordon: the Search ring was "hugging the text too
 * tightly, and needs corner rounding — all borders like this need on-brand corner
 * rounding". Reading `radius.sm` here (never a hardcoded literal) rounds the ring
 * on every inner form input to the same 4px the field container already uses.
 * Buttons/links already carry their own radius (8/4/9999/50%), so their rings
 * were never square — this token targets the inner-input defect class only.
 */
export const focusRingRadius = radius.sm;

/**
 * THE app-wide focus treatment, in one object, so a call site never has to
 * re-type a geometry. There are exactly TWO correct forms; pick by whether the
 * call site can afford to let `outline` carry the purple band.
 *
 *   FORM A — outline + inner band. Preferred. Matches what
 *   `src/styles/focusRing.css` does app-wide, and keeps the purple in `outline`
 *   where `forced-colors` can still reach it:
 *       outline: `${focusRing.width}px solid ${focusRing.color}`
 *       outlineOffset: focusRing.innerWidth
 *       boxShadow: focusRing.innerShadow            // + `, ${ownElevation}`
 *
 *   FORM B — `.shadow`, the complete double ring in one property. For a call
 *   site that has already set `outline: none` and owns its `box-shadow` slot.
 *   This is what `shadow.focus` resolves to, and therefore what 71 references
 *   across 27 files get without changing a line.
 *
 *   `.insetShadow` is FORM B drawn INWARD, for a control whose container clips
 *   it — an `overflow: hidden` ancestor eats an outward ring silently, and that
 *   silence is the failure mode, since nothing errors and the ring just is not
 *   there.
 *
 * All forms derive from the same four constants, so the white band, the purple
 * band and the outline offset can never drift apart the way three different
 * purples (#9B5AF8, #8126DB, #5A1B97) and three different widths did before —
 * all three of which were measured live in one header on 2026-08-02.
 */
export const focusRing = {
  color: focusRingColor,
  inner: focusRingInnerColor,
  width: focusRingWidth,
  innerWidth: focusRingInnerWidth,
  /** px. On-brand corner radius (radius.sm). The ring follows the focused
   *  element's border-radius, so a square inner <input> gets a square ring
   *  without this. See focusRingRadius above (DL-096). */
  radius: focusRingRadius,
  /** FORM A's white band. Pair with an `outline` at `outlineOffset: innerWidth`. */
  innerShadow: `0 0 0 ${focusRingInnerWidth}px ${focusRingInnerColor}`,
  /** FORM B. Both bands. */
  shadow:
    `0 0 0 ${focusRingInnerWidth}px ${focusRingInnerColor}, ` +
    `0 0 0 ${focusRingInnerWidth + focusRingWidth}px ${focusRingColor}`,
  insetShadow:
    `inset 0 0 0 ${focusRingInnerWidth}px ${focusRingInnerColor}, ` +
    `inset 0 0 0 ${focusRingInnerWidth + focusRingWidth}px ${focusRingColor}`,
};

/**
 * DATA-FIELD focus treatment — Gordon 2026-09-23 (REPEAT-COMPLAINT
 * gordon-0923-P1-REPEAT-kelly-field-focus-style-one-shared-border-only-app-
 * wide): every text-entry field (search boxes, TextFields, rich text) shows
 * exactly ONE effect on focus — its own border recolors. No outline, no
 * box-shadow, no second ring. This is DELIBERATELY DIFFERENT from
 * `focusRing` above, which stays the correct treatment for buttons, links
 * and icon controls (unchanged by this ruling).
 *
 * Mirrors `src/styles/dataFieldFocus.css` (the CSS-side primitive for plain
 * inputs/textareas/contenteditable) — this is the MUI-sx-side form, for any
 * `<TextField>`/`OutlinedInput`/`Autocomplete` caller. Value and mechanism
 * are the live-measured reference: the Edit Wheel "Wheel Name" Froala field
 * (`.froalaEditor--active .fr-box`, FroalaTextEditor/index.scss) — border-
 * color change only, width unchanged.
 */
export const dataFieldFocus = {
  color: focusRingColor,
  /** Spread into a MUI `<TextField sx={{...}}>` (or any `.MuiOutlinedInput-
   *  root` caller) to get the ONE shared focus treatment. Suppresses the
   *  inner input's own indicator and any stray wrapper box-shadow, then
   *  recolors just the notchedOutline border. */
  muiSx: {
    "& .MuiOutlinedInput-input:focus-visible:focus-visible": {
      outline: "none",
      boxShadow: "none",
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: "none",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      // CSS custom property, not the raw hex: resolves to the gallery
      // theme's own accent inside a themed gallery subtree (--gt-accent),
      // falling back to the flat brand purple everywhere else. Defined once
      // in src/styles/dataFieldFocus.css so the MUI (this file) and non-MUI
      // (that file) halves of this token can never drift apart.
      borderColor: "var(--iy-field-focus-color, " + focusRingColor + ")",
    },
  },
};

// ── Elevation / shadow ────────────────────────────────────────────────────────
// Kept intentionally subtle to match the app's light, flat surfaces.
export const shadow = {
  none: "none",
  e1: "0 1px 2px rgba(18, 48, 81, 0.06), 0 1px 3px rgba(18, 48, 81, 0.08)",
  e2: "0 2px 6px rgba(18, 48, 81, 0.08), 0 4px 12px rgba(18, 48, 81, 0.06)",
  e3: "0 6px 16px rgba(18, 48, 81, 0.10), 0 12px 28px rgba(18, 48, 81, 0.10)",
  // Derived, never re-typed — so the ring colour can never drift between
  // `shadow.focus` and `brand.focusRing` the way it did before.
  //
  // This is the DOUBLE ring as of 2026-08-02. It was `0 0 0 3px <purple>`, a
  // single flush band, which measured 2.06:1 against the "Skip to main content"
  // fill on live staging — the defect Gordon's white-inner-band decision closes.
  // 71 references across 27 files read this one value, so repointing it here is
  // what makes the treatment app-wide rather than a two-button patch.
  focus: focusRing.shadow,
  // For a control clipped by an `overflow: hidden` container. Three MediaLibrary
  // call sites used to write `inset ${shadow.focus}`, which — now that the value
  // is a LIST — would have applied `inset` to the first band only and left the
  // purple band outside the clip. Use this instead; never prefix `focus`.
  focusInset: focusRing.insetShadow,
};
// alias for MUI-style consumers
export const elevation = shadow;

// ── Motion ────────────────────────────────────────────────────────────────────
export const motion = {
  duration: {
    instant: "0ms",
    fast: "150ms",
    base: "200ms", // Button fade uses ~0.2s
    slow: "300ms",
  },
  easing: {
    standard: "cubic-bezier(0.2, 0, 0, 1)",
    emphasized: "cubic-bezier(0.2, 0, 0, 1.2)",
    linear: "linear",
    ease: "ease",
  },
  // Convenience for `transition:` shorthands honouring the base tempo.
  transition: (props = "all", duration = "200ms", easing = "cubic-bezier(0.2, 0, 0, 1)") =>
    `${props} ${duration} ${easing}`,
};

// ── Breakpoints ───────────────────────────────────────────────────────────────
// Re-exported from utils/device.js so tokens and media queries share one source.
export const breakpoints = {
  sm: breakpointSizes.sm, // 480px
  md: breakpointSizes.md, // 768px
  lg: breakpointSizes.lg, // 1024px
  xl: breakpointSizes.xl, // 1920px
};

// ── Z-index scale ─────────────────────────────────────────────────────────────
export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1100,
  // Persistent, low-priority chrome that must stay ABOVE ordinary page
  // content but can never be allowed to outrank anything the user is
  // actively interacting with (a tour step, a docked chat, a modal/popover).
  // Added for U11 (P2-O7 / P5S1-O6 / S2-Y9): ConsentBanner previously hardcoded
  // z-index 2147483000 — effectively "above everything, forever" — which let
  // it physically overlap and eat clicks on the onboarding tour's Next button
  // (features/firstViewWizard, z:header) and the DM dock's Send button
  // (components/ChatDock, z:header), and made it an uncontrolled extra layer
  // in the first-run overlay stack alongside the walkthrough/Upgrade popup.
  // Sits below `header` (and therefore below every real foreground control:
  // header/overlay/modal/popover/toast/tooltip all outrank it) so a banner
  // can be visible without ever winning a stacking fight it has no business
  // winning. See src/components/ConsentBanner/ConsentBanner.css.
  banner: 1150,
  header: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  toast: 1600,
  tooltip: 1700,
};

// ── Brand Wheel palette (see components/BrandWheel) ────────────────────────────
// Canonical jewel palette + representative unequal spans, kept here so the token
// layer owns the brand-wheel identity too. The renderer lives in BrandWheel.
export const brandWheel = {
  colors: ["#9B5AF8", "#6D5EF6", "#34D399", "#F59E0B", "#F472B6", "#22D3EE", "#A78BFA"],
  palette: ["#9B5AF8", "#34D399", "#F59E0B", "#F472B6", "#22D3EE", "#6D5EF6"],
  spans: [66, 50, 72, 44, 62, 66],
  ratio: 0.75, // Portrait/inner radius ratio — do not change (app law)
};

// ── Brand action colors (Fable elevation ELEVATED-DESIGN-LANGUAGE.md §1) ──────
// The brand purple #9B5AF8 is the app's identity color and stays for ACCENTS
// (icons, switches, focus, outlined-add borders). For a filled PRIMARY action
// (Save / Continue / the solid CTA), the elevation darkens the fill to
// `primaryActive` #8F38EF so white label text clears WCAG-AA (5.28:1) — the
// undarkened #9B5AF8 on white is only 4.03:1 and FAILS AA for 16px text.
// `primaryActiveHover` is the pressed/hover step. Single source of truth so no
// screen re-hardcodes the darkened purple.
export const brand = {
  primary: "#9B5AF8", // accent / identity (matches colors.PrimaryPurple)
  primaryActive: "#8F38EF", // filled primary fill — AA 5.28:1 on white
  primaryActiveHover: "#7E28E0", // pressed / hover step
  // The keyboard focus ring. Reads the single source of truth above — see the
  // measured contrast table there for why this is #9B5AF8 and not #CBB4FE.
  focusRing: focusRingColor,
};

// ── Form-field semantics (single source of truth for the shared EditorField) ──
// Before ENG-C the block-editor framework re-declared these exact hexes as local
// `const INK/MUTED/PURPLE/BORDER` in 6+ files (controls.jsx, EditorSection.jsx,
// every editor…), plus three different error reds and ad-hoc height literals —
// the same drift the audit flagged for the legacy editors (register UI-058/062/
// 063/064/066). This block centralises field chrome so `<EditorField>` and every
// control read ONE set of values. Colours mirror the existing palette where one
// exists (ink == colors.textBlue #123051, muted == colors.StrokePrimary #667085,
// accent == brand.primary) and add only the field-specific neutrals the palette
// never named (border, placeholder) + ONE canonical error red. Metrics enforce
// the ≥44px touch target + 8px control radius the design system already uses.
export const field = {
  // colour
  ink: "#123051",          // label + typed value  (== colors.textBlue / primary)
  muted: "#667085",        // helper text + counter (== colors.StrokePrimary)
  border: "#E4E7EC",       // resting field border  (neutral, palette had none)
  borderHover: "#C8CDD6",  // hover border
  borderInvalid: "#D92D20",// invalid border == error
  errorText: "#D92D20",    // THE one field-error red (kills the 3-reds drift, UI-062)
  placeholder: "#98A2B3",  // input placeholder
  bg: "#FFFFFF",           // field surface
  bgDisabled: "#F2F4F7",   // disabled surface
  accent: brand.primary,   // focus/active accent (#9B5AF8)
  focusShadow: "0 0 0 3px #EDE4FE", // soft focus fill shared across controls
  // metric
  minHeight: 44,           // a11y touch target (px)
  textareaMinHeight: 96,   // multiline default (replaces ad-hoc 70/120 literals)
  radius: radius.md,       // 8px control radius
  gap: spacing.xs,         // 4px label→control gap
  labelSize: 12,           // label px
  helpSize: 11,            // helper/counter/error px
};

// ── Circular icon-overlay (DESIGN RULING, Gordon 2026-08-19) ──────────────────
// C:\iYuga\specs\circular-icon-overlay-ruling.md.
// The painted circular background BEHIND an icon-button — the kebab (3-dot), the
// close X, and any similar icon with a circular overlay — must be SMALL and the
// SAME everywhere: a tight ring whose diameter is the icon glyph's height +~10%
// (`ratio` 1.1), never a big disc. This is the SINGLE source of truth so every
// kebab / X / etc inherits ONE value — never a per-instance hard-coded size.
//
// HARD CONSTRAINT — the click-surface-touch-target ruling (>=32px tappable) STILL
// applies. The small circle is VISUAL only; keep the tappable/hit area >=32px via
// TRANSPARENT hit-padding AROUND the small painted circle. Shrink the painted
// circle, NEVER the touch target. `minHitTarget` (32px) is that floor.
//
// Colour/opacity of the overlay is unchanged by this token — it governs GEOMETRY
// only (diameter + hit area). Consume via the shared <OverlayIconButton>
// (src/components/OverlayIconButton), which reads these values.
export const iconOverlay = {
  /** Painted-circle diameter as a multiple of the icon glyph height (icon +~10%). */
  ratio: 1.1,
  /** px. Minimum tappable/hit area — the click-surface-touch-target ruling. */
  minHitTarget: 32,
  /**
   * px diameter of the painted circle for a given icon glyph height (px number).
   * Rounded so it lands on a whole pixel. e.g. diameter(20) -> 22, diameter(24) -> 26.
   */
  diameter: (iconSize) => Math.round(iconSize * 1.1),
  /**
   * px hit-target for a given icon glyph height: the painted circle, floored at
   * `minHitTarget` so the tappable area is never below 32px however small the icon.
   */
  hitTarget: (iconSize) => Math.max(32, Math.round(iconSize * 1.1)),
  /** CSS `calc()` form for a styled/sx context where iconSize is a CSS length. */
  diameterCss: (iconSize = "20px") => `calc(${iconSize} * 1.1)`,
};

// ── Hover-highlight (register bug #909) ────────────────────────────────────────
// Register bug #909: hover-highlighted rows/dropdown-items were inconsistent
// app-wide — some had too little (or zero) gap between the text and the
// highlight edge, and highlight corners didn't match their container's corner
// radius. Worst offender: the "Comments" accordion row (CommentSection) zeroed
// its own left/right padding so the header text would line up with the card,
// which left the purple hover fill flush against the text with no gap at all.
//
// ONE spec, so every hover-highlighted row/menu-item/dropdown-item shares the
// same text↔edge padding. `radius.sm` (4px, the app's own "controls" radius —
// see the radius scale comment above) is the default highlight radius: a
// hover-highlighted row is a control nested inside a rounded card/menu/dialog,
// and 4px stays safely inside the nested-radius law below for every measured
// inset in the app (>=4px), unlike the 6/7/8px (or missing) radii it replaces.
//
// NESTED-RADIUS LAW: for two concentric rounded rects, the inner radius must
// equal the outer radius minus the inner element's inset from the outer edge,
// or the two curves stop being concentric and the inner corner visibly pokes
// past (or falls short of) the outer curve. `innerRadius` computes that, and
// floors at 0 so it never goes negative for a tightly-inset row.
export const hoverHighlight = {
  paddingX: spacing.md, // 12px — horizontal gap between the highlighted edge and the text
  paddingY: spacing.sm, // 8px — vertical gap between the highlighted edge and the text
  radius: radius.sm, // 4px — default highlight corner radius (see rationale above)
  /** px. Nested-radius law: inner = outer - inset, floored at 0/radius.none. */
  innerRadius: (outerRadius, inset) => Math.max(radius.none, outerRadius - inset),
  // gordon-0922-P1-discover-trending-kebab-menu-clone-flash-share-delay-hover:
  // the on-brand fill for a hovered menu-item ROW (Clone/Share/Report, ...).
  // A 12% tint of brand.primary (#9B5AF8 -> rgba(155, 90, 248, 0.12)), the same
  // alpha step WheelCardThreeDotMenu's own kebab-BUTTON hover overlay already
  // uses (rgba(17, 24, 39, 0.12)) — just swapped to the brand hue so a hovered
  // row reads as ON-BRAND instead of @szhsin/react-menu's generic library
  // default (`.szh-menu__item--hover { background-color: #ebebeb; }`, which
  // MEASURED live on web-dev.iyuga.net is also inconsistently present at all —
  // it only paints when some OTHER, unrelated component happens to import
  // `@szhsin/react-menu/dist/index.css` into the same route's bundle).
  background: "rgba(155, 90, 248, 0.12)",
};

// ── Kebab (3-dot "more options") trigger + menu ────────────────────────────────
// ISP-9999 gordon-0920-kebab-icon-and-kebab-menu-sizes-differ-profile-card-vs-
// primary-wheel-card-app-wide. Owner report 2026-09-20 08:00 PT, screenshot
// ~/qa-notes/gordon-0920-kebab-sizes/owner-screenshot.png: "Profile Box and
// Primary Wheel kebabs look different sizes". MEASURED (source-level, inline
// styles = computed styles, no overriding stylesheet rule found) on
// web-dev.iyuga.net before this fix:
//   Profile card "Profile options" kebab  (WheelGallery/Profile/index.jsx):
//     icon = react-icons FaEllipsisV, inline style width:8px height:16px
//   LinkVault chip kebab (ProfileCardV2.jsx):
//     icon = react-icons FaEllipsisV, inline style width:8px height:16px
//   Primary Wheel / wheel-grid / collection-tile kebabs (WheelCardThreeDotMenu,
//   KebabContextMenu, FoldersThreeDotMenu — already consistent with EACH OTHER):
//     icon = MUI MoreVert, fontSize="small" (20px)
// ROOT CAUSE: two entirely different icon FAMILIES for the same "more options"
// affordance (a 5-bar vertical ellipsis glyph vs. MUI's 3-dot MoreVert glyph),
// not just a size mismatch — the Profile/LinkVault instances never read this
// token or the majority implementation's icon choice at all.
// This block is the ONE size a caller should use going forward. `iconSize`
// matches the ALREADY-majority MoreVert `fontSize="small"` implementations
// (WheelCardThreeDotMenu/KebabContextMenu/FoldersThreeDotMenu) rather than
// inventing a new number, so those three files' rendered output is BYTE-
// IDENTICAL before and after this token existed — only the previously-
// divergent FaEllipsisV instances (Profile card, LinkVault chip, Business Box
// builder pane) move onto it.
// `hitArea` has two named variants (not a free-standing per-page number — no
// design-deck page was available to name others, so only the two variants
// ALREADY established by existing, commented design intent are kept):
//   compact (32px) — the click-surface-touch-target ruling floor
//     (iconOverlay.minHitTarget), used when the kebab sits alone on a small
//     card/tile corner (collection tile, LinkVault chip).
//   row (44px) — used when the kebab sits in a row of other 44px icon
//     buttons (UserActions Follow/Message parity — see WheelCardThreeDotMenu's
//     own "D19" comment for why that number is load-bearing there).
export const kebab = {
  iconSize: 20,
  hitArea: {
    compact: iconOverlay.minHitTarget, // 32
    row: 44,
  },
  menu: {
    minWidth: 200,
    radius: radius.md, // 8
    border: "1px solid #ddd",
    shadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    background: "#fff",
  },
  item: {
    paddingX: hoverHighlight.paddingX, // 12
    paddingY: hoverHighlight.paddingY, // 8
    radius: hoverHighlight.radius, // 4
    fontSize: typography.largeText.fontSize, // 14
    fontFamily,
  },
};

// ── Tier badge colours (TIER BADGE LAW, Gordon 2026-09-15) ─────────────────────
// Single source of truth for every "you need tier X" indicator app-wide (the
// Build-menu (P) tile, Portrait media type locks, poll/form headers, ...).
// Essential = blue (unchanged) · Premium = purple (PURPLE IS PREMIUM-ONLY —
// several ad-hoc badges wrongly reused it for ProSuite) · ProSuite = on-brand
// gold, matching the existing ProSuite plan-card gradient's base tone
// (SubscriptionCard.styled.jsx: #FBCF21 -> #FFBE07 -> #FF9C00 -> #ED8900) and
// utils/tierTooltip.js's own TIER_TOOLTIP_COLOR, so a badge and the tooltip/
// upgrade-card it points at never disagree.
export const tierColor = {
  essential: "#325AE9",
  premium: "#9256E7",
  prosuite: "#FF9C00",
};

// gordon-0920-premium-tier-badge-letter-optical-centering-app-wide: TierBadge
// centers its letter with `display:flex; align-items/justify-content:center;
// line-height:1` — the right recipe to avoid layout-level centering bugs
// (no baseline drift, no magic padding), but a browser still centers the
// text's LINE BOX using the font's ascent/descent metrics, which allot space
// for descenders these all-caps glyphs never use. Measured (isolated harness:
// real Chromium layout + DM Sans, badge screenshotted and ink-scanned the
// same way as PremiumSubscriptionIcon.opticalCenter.test.js, background
// masked to the circle interior, TierBadge's default 16px size, DPR 1 & 2):
// every letter's ink centroid sits up-and-left of the circle center by an
// amount that scales with font-size, hence expressed here in em so the
// offset scales with `size` automatically:
//   E:  dx +0.05em  dy +0.05em   (centroid measured ~-0.5px up/left at 10px font)
//   P:  dx +0.05em  dy +0.07em   (~-0.46px left / -0.68px up at 10px font)
//   Ps: dx +0.11em  dy +0.09em   (~-1.08px left / -0.87px up -- the trailing
//                                 lowercase "s" pulls the two-glyph ink mass
//                                 further left than either "E" or "P" alone)
// Sign convention: positive x moves the glyph right, positive y moves it down
// (opposite the measured negative/up-left bias), applied via a CSS translate
// on the glyph only -- see TierBadge/index.jsx.
export const tierBadgeOpticalOffset = {
  E: { x: 0.05, y: 0.05 },
  P: { x: 0.05, y: 0.07 },
  Ps: { x: 0.11, y: 0.09 },
};

// ── The aggregate token object (single import for consumers) ──────────────────
export const tokens = {
  fontFamily,
  fontWeight,
  typography,
  spacing,
  spaceUnit,
  space,
  radius,
  shadow,
  elevation,
  motion,
  breakpoints,
  zIndex,
  brandWheel,
  brand,
  field,
  focusRing,
  dataFieldFocus,
  iconOverlay,
  hoverHighlight,
  kebab,
  tierColor,
  tierBadgeOpticalOffset,
};

export default tokens;
